import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Star, Loader2, MessageSquare, ThumbsUp, BookOpen, Share2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function StarRow({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-4 py-1.5">
      <span className="text-sm text-stone-700">{label}</span>
      <div className="flex gap-1" data-testid={testid}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            className="p-0.5"
            data-testid={`${testid}-${n}`}
            aria-label={`${label} ${n} stars`}
          >
            <Star
              className={`h-5 w-5 ${
                n <= (value || 0) ? "fill-amber-400 text-amber-400" : "text-stone-300"
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
}


export default function SatisfactionCard({ degreeId }) {
  const { user, login } = useAuth();
  const [agg, setAgg] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    overall_rating: 0, teaching_rating: 0, campus_rating: 0, value_rating: 0,
    would_recommend: null, review: "", reviewer_display_name: "",
  });

  const load = () => {
    api.get(`/satisfaction/degree/${degreeId}`)
      .then((r) => setAgg(r.data))
      .catch(() => setAgg(null));
  };

  useEffect(() => { load(); }, [degreeId]);

  if (!agg) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.info("Sign in to submit a review — we'll bring you back here.");
      login();
      return;
    }
    if (!form.overall_rating) {
      toast.error("Please rate the overall experience.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/satisfaction/submit", { degree_id: degreeId, ...form });
      toast.success("Thank you — your review is live.");
      setShowForm(false);
      setForm({
        overall_rating: 0, teaching_rating: 0, campus_rating: 0,
        value_rating: 0, would_recommend: null, review: "", reviewer_display_name: "",
      });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your review.");
    } finally {
      setSubmitting(false);
    }
  };

  const av = agg.averages;
  const realReviews = agg.recent_reviews || [];
  const editorialReviews = agg.editorial_reviews || [];
  const editorialCount = agg.editorial_count || 0;

  return (
    <div
      data-testid="satisfaction-card"
      className="mt-10 rounded-lg border border-stone-300 bg-white p-6"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-uppercase text-stone-500">Student satisfaction</div>
          <h3 className="font-editorial text-2xl mt-1">
            {agg.count === 0
              ? "Be the first to review this course."
              : `${agg.count} student${agg.count === 1 ? "" : "s"} reviewed this course.`}
          </h3>
          {av && (
            <div className="mt-2 flex flex-wrap gap-4 text-sm text-stone-700"
                 data-testid="satisfaction-averages">
              <span>Overall <strong>{av.overall}/5</strong></span>
              {av.teaching !== null && <span>Teaching <strong>{av.teaching}/5</strong></span>}
              {av.value !== null && <span>Value <strong>{av.value}/5</strong></span>}
              {av.campus !== null && <span>Campus <strong>{av.campus}/5</strong></span>}
              {typeof av.recommend_pct === "number" && (
                <span className="inline-flex items-center gap-1 text-emerald-700">
                  <ThumbsUp className="h-3.5 w-3.5" /> {av.recommend_pct}% recommend
                </span>
              )}
            </div>
          )}
          {agg.count === 0 && editorialCount > 0 && (
            <p className="mt-2 text-[12px] text-stone-500 max-w-lg leading-snug">
              No verified student reviews yet — {editorialCount} editorial{" "}
              note{editorialCount === 1 ? " is" : "s are"} shown below while
              we collect real submissions.
            </p>
          )}
        </div>
        <div className="flex flex-col gap-2 items-end shrink-0">
          <Link
            to={`/survey/${degreeId}`}
            data-testid="satisfaction-public-survey"
            className="inline-flex items-center gap-1.5 text-sm font-underground-sc tracking-wider text-white bg-[#4C1D95] hover:bg-[#3B0764] px-3 py-2 rounded-full transition-colors"
          >
            <Share2 className="h-3.5 w-3.5" />
            Studied here? Share your view
          </Link>
          {user && !showForm && (
            <Button
              variant="outline"
              size="sm"
              data-testid="satisfaction-open-form"
              onClick={() => setShowForm(true)}
            >
              <MessageSquare className="h-4 w-4 mr-1.5" />
              Signed-in quick review
            </Button>
          )}
        </div>
      </div>

      {showForm && (
        <form onSubmit={submit} className="mt-6 border-t border-stone-200 pt-6"
              data-testid="satisfaction-form">
          <div className="text-[11px] font-underground-sc tracking-wider text-emerald-700 mb-2">
            Signed-in shortcut · publishes instantly, no moderation queue
          </div>
          <StarRow label="Overall experience *" value={form.overall_rating}
                   onChange={(n) => setForm((s) => ({ ...s, overall_rating: n }))}
                   testid="stars-overall" />
          <StarRow label="Teaching quality" value={form.teaching_rating}
                   onChange={(n) => setForm((s) => ({ ...s, teaching_rating: n }))}
                   testid="stars-teaching" />
          <StarRow label="Value for money" value={form.value_rating}
                   onChange={(n) => setForm((s) => ({ ...s, value_rating: n }))}
                   testid="stars-value" />
          <StarRow label="Campus & city life" value={form.campus_rating}
                   onChange={(n) => setForm((s) => ({ ...s, campus_rating: n }))}
                   testid="stars-campus" />
          <div className="flex items-center gap-3 py-1.5">
            <span className="text-sm text-stone-700">Would you recommend it?</span>
            <div className="flex gap-2">
              <button type="button" data-testid="recommend-yes"
                      onClick={() => setForm((s) => ({ ...s, would_recommend: true }))}
                      className={`px-3 py-1 rounded-full text-xs font-underground-sc border
                        ${form.would_recommend === true
                          ? "bg-emerald-700 text-white border-emerald-700"
                          : "bg-white border-stone-300 text-stone-700"}`}>Yes</button>
              <button type="button" data-testid="recommend-no"
                      onClick={() => setForm((s) => ({ ...s, would_recommend: false }))}
                      className={`px-3 py-1 rounded-full text-xs font-underground-sc border
                        ${form.would_recommend === false
                          ? "bg-stone-900 text-white border-stone-900"
                          : "bg-white border-stone-300 text-stone-700"}`}>No</button>
            </div>
          </div>

          <div className="mt-4">
            <Label htmlFor="rev-name">Display name (optional)</Label>
            <Input id="rev-name" data-testid="review-name"
                   value={form.reviewer_display_name}
                   onChange={(e) => setForm((s) => ({ ...s, reviewer_display_name: e.target.value }))}
                   placeholder="Alex M. — we never publish your email" />
          </div>
          <div className="mt-4">
            <Label htmlFor="rev-body">Your review</Label>
            <Textarea id="rev-body" rows={4}
                      data-testid="review-body"
                      value={form.review}
                      onChange={(e) => setForm((s) => ({ ...s, review: e.target.value }))}
                      placeholder="What did you enjoy? What surprised you? What would you tell a prospective applicant?" />
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Button type="submit" disabled={submitting} data-testid="satisfaction-submit"
                    className="bg-[#4C1D95] hover:bg-[#3B0764] text-white">
              {submitting ? (<><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…</>)
                          : "Publish review"}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      {realReviews.length > 0 && (
        <div className="mt-6 border-t border-stone-200 pt-6 space-y-4"
             data-testid="satisfaction-recent">
          <div className="label-uppercase text-stone-500 flex items-center gap-2">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-600" />
            Real student reviews
          </div>
          {realReviews.map((r) => (
            <div key={r.id} className="border-l-2 border-emerald-500 pl-4">
              <div className="flex items-center gap-2 text-xs text-stone-500 flex-wrap">
                <span className="font-mono">{r.reviewer_display_name || "Anonymous"}</span>
                {r.graduation_year && <span>· grad {r.graduation_year}</span>}
                <span>·</span>
                <span>Overall {r.overall_rating}/5</span>
                {r.would_recommend && (
                  <span className="text-emerald-700 inline-flex items-center gap-0.5">
                    · <ThumbsUp className="h-3 w-3" /> recommends
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm text-stone-800">{r.review}</p>
            </div>
          ))}
        </div>
      )}

      {editorialReviews.length > 0 && (
        <div className="mt-6 border-t border-stone-200 pt-6 space-y-4"
             data-testid="satisfaction-editorial">
          <div className="label-uppercase text-stone-500 flex items-center gap-2">
            <BookOpen className="h-3.5 w-3.5 text-stone-500" />
            Editorial notes
            <span className="text-[10px] text-stone-400 normal-case tracking-normal">
              curated by our team while we collect verified student reviews
            </span>
          </div>
          {editorialReviews.map((r) => (
            <div key={r.id} className="border-l-2 border-stone-300 pl-4">
              <div className="flex items-center gap-2 text-xs text-stone-500 flex-wrap">
                <span className="font-mono">{r.reviewer_display_name || "Editorial"}</span>
                <span>·</span>
                <span>Overall {r.overall_rating}/5</span>
                {r.would_recommend && (
                  <span className="text-emerald-700 inline-flex items-center gap-0.5">
                    · <ThumbsUp className="h-3 w-3" /> recommends
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm text-stone-800">{r.review}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
